import React, { useState, useEffect, useRef } from 'react';
import { Edit2, Trash2, Loader2, FileText, Download, Star } from 'lucide-react';
import { toast } from 'sonner';
import AdminLogin from './AdminLogin';
import AdminHeader from './AdminHeader';
import ResourceForm from './admin/ResourceForm';
import { useAdminAuth } from '../hooks/useAdminAuth';
import { apiUrl, API_URL } from '../utils/config';
import logger from '../utils/logger';

const ResourcesAdmin = ({ onBackToHome }) => {
  const { isAuth, isCheckingAuth, handleLogin, handleLogout } = useAdminAuth();
  
  const [resources, setResources] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingResource, setEditingResource] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('en');
  const [isUploading, setIsUploading] = useState(false);
  const [isUploadingImage, setIsUploadingImage] = useState(false);
  
  const fileInputRef = useRef(null);
  const imageInputRef = useRef(null);

  const [formData, setFormData] = useState({
    title_en: '', title_tr: '', description_en: '', description_tr: '',
    category: '', fileUrl: '', image: '', pages: '', featured: false
  });

  const fetchResources = async () => {
    setIsLoading(true);
    try {
      const response = await fetch(apiUrl("library/resources/admin"));
      if (response.ok) {
        const data = await response.json();
        setResources(data);
      }
    } catch (error) {
      logger.error('Error fetching resources:', error);
      toast.error('Failed to load resources');
    }
    setIsLoading(false);
  };

  useEffect(() => {
    if (isAuth) fetchResources();
  }, [isAuth]);

  const resetForm = () => {
    setFormData({
      title_en: '', title_tr: '', description_en: '', description_tr: '',
      category: '', fileUrl: '', image: '', pages: '', featured: false
    });
    setEditingResource(null);
    setActiveTab('en');
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.includes('pdf')) { toast.error('Please upload a PDF file'); return; }
    if (file.size > 20 * 1024 * 1024) { toast.error('File size must be less than 20MB'); return; }

    setIsUploading(true);
    const fd = new FormData();
    fd.append('file', file);
    try {
      const response = await fetch(apiUrl("upload-pdf"), { method: 'POST', body: fd });
      if (response.ok) {
        const data = await response.json();
        setFormData(prev => ({ ...prev, fileUrl: `${API_URL}${data.url}` }));
        toast.success('PDF uploaded successfully!');
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to upload PDF');
      }
    } catch (error) {
      logger.error('Upload error:', error);
      toast.error('Failed to upload PDF');
    }
    setIsUploading(false);
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) { toast.error('Please upload an image file'); return; }
    if (file.size > 5 * 1024 * 1024) { toast.error('Image size must be less than 5MB'); return; }

    setIsUploadingImage(true);
    const fd = new FormData();
    fd.append('file', file);
    try {
      const response = await fetch(apiUrl("upload"), { method: 'POST', body: fd });
      if (response.ok) {
        const data = await response.json();
        setFormData(prev => ({ ...prev, image: `${API_URL}${data.url}` }));
        toast.success('Cover image uploaded successfully!');
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to upload image');
      }
    } catch (error) {
      logger.error('Image upload error:', error);
      toast.error('Failed to upload image');
    }
    setIsUploadingImage(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.title_en.trim()) { toast.error('English title is required'); return; }
    if (!formData.description_en.trim()) { toast.error('English description is required'); return; }
    if (!formData.category) { toast.error('Category is required'); return; }
    if (!formData.fileUrl) { toast.error('Please upload a PDF file'); return; }

    setIsSaving(true);
    const resourceData = {
      title_en: formData.title_en, title_tr: formData.title_tr || null,
      description_en: formData.description_en, description_tr: formData.description_tr || null,
      category: formData.category, fileUrl: formData.fileUrl, image: formData.image || null,
      pages: formData.pages ? parseInt(formData.pages) : null, featured: formData.featured
    };

    try {
      const url = editingResource ? apiUrl(`library/resources/${editingResource.id}`) : apiUrl("library/resources");
      const response = await fetch(url, {
        method: editingResource ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(resourceData)
      });
      if (response.ok) {
        toast.success(editingResource ? 'Resource updated!' : 'Resource created!');
        setShowForm(false);
        resetForm();
        fetchResources();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to save resource');
      }
    } catch (error) {
      logger.error('Error saving resource:', error);
      toast.error('Failed to save resource');
    }
    setIsSaving(false);
  };

  const handleEdit = (resource) => {
    setEditingResource(resource);
    setFormData({
      title_en: resource.title_en || '', title_tr: resource.title_tr || '',
      description_en: resource.description_en || '', description_tr: resource.description_tr || '',
      category: resource.category || '', fileUrl: resource.fileUrl || '', image: resource.image || '',
      pages: resource.pages?.toString() || '', featured: resource.featured || false
    });
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this resource?')) return;
    try {
      const response = await fetch(apiUrl(`library/resources/${id}`), { method: 'DELETE' });
      if (response.ok) { toast.success('Resource deleted!'); fetchResources(); }
      else toast.error('Failed to delete resource');
    } catch (error) {
      logger.error('Error deleting resource:', error);
      toast.error('Failed to delete resource');
    }
  };

  if (isCheckingAuth) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  if (!isAuth) return <AdminLogin onLogin={handleLogin} title="Resources Admin" />;

  const stats = {
    total: resources.length,
    featured: resources.filter(r => r.featured).length,
    categories: [...new Set(resources.map(r => r.category))].length
  };

  return (
    <div className="min-h-screen bg-gray-50" data-testid="resources-admin">
      <AdminHeader
        title="Resources Admin"
        onBackToHome={onBackToHome}
        onLogout={handleLogout}
        onAddNew={() => { resetForm(); setShowForm(true); }}
        addNewLabel="New Resource"
      />

      <div className="container mx-auto px-6 py-8">
        {/* Resource List */}
        <div className="bg-white rounded-xl shadow-sm overflow-hidden">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="text-left px-6 py-4 text-sm font-semibold text-gray-900">Resource</th>
                <th className="text-left px-6 py-4 text-sm font-semibold text-gray-900">Category</th>
                <th className="text-left px-6 py-4 text-sm font-semibold text-gray-900">Pages</th>
                <th className="text-left px-6 py-4 text-sm font-semibold text-gray-900">Status</th>
                <th className="text-right px-6 py-4 text-sm font-semibold text-gray-900">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {isLoading ? (
                <tr><td colSpan="5" className="px-6 py-12 text-center"><Loader2 className="w-8 h-8 animate-spin text-blue-600 mx-auto" /></td></tr>
              ) : resources.length === 0 ? (
                <tr><td colSpan="5" className="px-6 py-12 text-center text-gray-500">
                  <FileText className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                  <p>No resources yet. Click "New Resource" to add one.</p>
                </td></tr>
              ) : (
                resources.map((resource) => (
                  <tr key={resource.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <div className="p-2 bg-red-50 rounded-lg"><FileText className="w-5 h-5 text-red-600" /></div>
                        <div>
                          <p className="font-medium text-gray-900">{resource.title_en}</p>
                          {resource.title_tr && <p className="text-sm text-gray-500">TR available</p>}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4"><span className="px-3 py-1 bg-blue-50 text-blue-700 text-sm rounded-full">{resource.category}</span></td>
                    <td className="px-6 py-4 text-gray-600">{resource.pages ? `${resource.pages} pages` : '-'}</td>
                    <td className="px-6 py-4">
                      {resource.featured && (
                        <span className="px-3 py-1 bg-yellow-100 text-yellow-700 text-xs rounded-full inline-flex items-center gap-1">
                          <Star className="w-3 h-3" /> Featured
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center justify-end gap-2">
                        <a href={resource.fileUrl} target="_blank" rel="noopener noreferrer"
                          className="p-2 hover:bg-gray-100 rounded-lg transition-colors text-gray-500 hover:text-blue-600" title="Download PDF">
                          <Download className="w-4 h-4" />
                        </a>
                        <button onClick={() => handleEdit(resource)}
                          className="p-2 hover:bg-gray-100 rounded-lg transition-colors text-gray-500 hover:text-blue-600" title="Edit">
                          <Edit2 className="w-4 h-4" />
                        </button>
                        <button onClick={() => handleDelete(resource.id)}
                          className="p-2 hover:bg-gray-100 rounded-lg transition-colors text-gray-500 hover:text-red-600" title="Delete">
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-8">
          <div className="bg-white rounded-xl p-6 shadow-sm">
            <div className="text-3xl font-bold text-blue-600">{stats.total}</div>
            <div className="text-gray-600">Total Resources</div>
          </div>
          <div className="bg-white rounded-xl p-6 shadow-sm">
            <div className="text-3xl font-bold text-yellow-600">{stats.featured}</div>
            <div className="text-gray-600">Featured</div>
          </div>
          <div className="bg-white rounded-xl p-6 shadow-sm">
            <div className="text-3xl font-bold text-green-600">{stats.categories}</div>
            <div className="text-gray-600">Categories</div>
          </div>
        </div>
      </div>

      {/* Form Modal */}
      {showForm && (
        <ResourceForm
          formData={formData}
          setFormData={setFormData}
          editingResource={editingResource}
          activeTab={activeTab}
          setActiveTab={setActiveTab}
          onSubmit={handleSubmit}
          onClose={() => { setShowForm(false); resetForm(); }}
          isSaving={isSaving}
          isUploading={isUploading}
          isUploadingImage={isUploadingImage}
          fileInputRef={fileInputRef}
          imageInputRef={imageInputRef}
          onFileUpload={handleFileUpload}
          onImageUpload={handleImageUpload}
        />
      )}
    </div>
  );
};

export default ResourcesAdmin;
